// Guns held in the world, painted at their on-screen size (1 canvas px = 1 hd px, like runner-detail.ts) instead of
// being drawn at 2x and resampled. One silhouette per gun family (weapon id), east-facing with the hand at `grip`;
// the rig flips the sprite vertically when aiming west so sights stay on top. Optional fitted parts (optic,
// suppressor, extended magazine) sit on per-family mount points. Colours come from hd-kit (theme palette).
import { canvas } from './art';
import { K, px, dk, lt, mix } from './hd-kit';
import type { WeaponKind } from './character-motion';

/** `?guns=0` shows the old resampled weapon drawing for comparison. */
export const HELD_GUNS = typeof location === 'undefined' || new URLSearchParams(location.search).get('guns') !== '0';
export interface HeldParts { optic?: boolean; suppressor?: boolean; extMag?: boolean }
/** PlayerSnap.gp bits (gunsmith.ts heldPartBits: 1 optic, 2 suppressor, 4 extended mag) as fitted parts. */
export const partsFromBits = (bits = 0): HeldParts => ({ optic: !!(bits & 1), suppressor: !!(bits & 2), extMag: !!(bits & 4) });
export interface HeldGun { canvas: HTMLCanvasElement; grip: { x: number; y: number }; muzzle: { x: number; y: number } }
type G = CanvasRenderingContext2D;
type R = (x: number, y: number, w: number, h: number, c: string) => void;

/** Weapon ids with their own held silhouette. Unknown ids fall back by kind. */
export const HELD_FAMILIES = ['pistol', 'p11', 'smart', 'k5', 's10', 'v47', 'vs12', 'charge'] as const;
export type HeldFamily = typeof HELD_FAMILIES[number];
const BY_KIND: Partial<Record<WeaponKind, HeldFamily>> = { pistol: 'pistol', smg: 'smart', rifle: 'v47', shotgun: 'vs12', coilgun: 'charge' };
export function heldFamily(id: string, kind: WeaponKind): HeldFamily | undefined {
  return (HELD_FAMILIES as readonly string[]).includes(id) ? id as HeldFamily : BY_KIND[kind];
}

const W = 56, H = 24, OX = 20, OY = 13; // canvas and hand origin
const hi = K.metalHi, m = K.metal, lo = K.metalLo, ink = K.ink;
const poly = mix(K.night, K.slate, 0.4), polyHi = lt(poly, 0.25), polyLo = dk(poly, 0.35);
const wood = dk(K.amber, 0.42), woodHi = dk(K.amber, 0.2), woodLo = dk(K.amber, 0.62);

/** Pistol grip: a 3 px column under the hand, raked back `rake` px over its height, lit on the leading edge. */
function grip(r: R, x: number, y: number, h: number, col: string, colHi: string, rake = 1) {
  for (let i = 0; i < h; i++) { const dx = -Math.floor(i * rake / Math.max(1, h - 1)); r(x + dx, y + i, 3, 1, col); r(x + dx + 2, y + i, 1, 1, colHi); }
}
function guard(r: R, x: number, y: number) { r(x, y + 2, 4, 1, lo); r(x + 3, y, 1, 2, lo); r(x + 1, y, 1, 1, hi); }

interface Mounts { top: [number, number]; muzzle: [number, number] }
/** Each family paints itself and returns where an optic sits (x, rail y) and the muzzle (x, bore y). */
const FAMILY: Record<HeldFamily, (r: R, magOut: boolean, ext: boolean) => Mounts> = {
  // Kenji P-9 Carom (original): a flat slab slide with the cyan rear sight, pink ricochet strip on the frame.
  pistol(r) {
    r(-4, -5, 15, 3, m); r(-4, -5, 15, 1, hi); r(-4, -3, 15, 1, lo);
    r(-3, -4, 1, 1, ink); r(-1, -4, 1, 1, ink); r(3, -4, 2, 1, ink);
    r(-3, -6, 1, 1, K.cyan); r(9, -6, 1, 1, hi);
    r(-2, -2, 11, 1, lo); r(4, -2, 3, 1, K.pink);
    guard(r, 1, -1); grip(r, -2, -1, 5, lo, m); r(-3, 4, 3, 1, ink);
    return { top: [0, -6], muzzle: [11, -4] };
  },
  // P-11 Hayate: a longer two-tone pistol, metal slide with lightening cuts over a polymer frame, raked stippled grip.
  p11(r, _o, ext) {
    r(-4, -5, 16, 2, m); r(-4, -5, 16, 1, hi); r(5, -4, 1, 1, ink); r(7, -4, 1, 1, ink); r(9, -4, 1, 1, ink);
    r(-4, -3, 15, 2, poly); r(-4, -3, 15, 1, polyHi); r(8, -2, 1, 1, ink); r(10, -2, 1, 1, ink);
    r(-3, -6, 1, 1, hi); r(10, -6, 1, 1, K.amber);
    guard(r, 1, -1); grip(r, -2, -1, ext ? 7 : 5, poly, polyHi, 2); r(-1, 1, 1, 1, polyLo); r(-2, 3, 1, 1, polyLo);
    if (ext) r(-4, 5, 3, 1, lo);
    return { top: [-1, -6], muzzle: [12, -4] };
  },
  // Homer-7 Smart SMG (original): a boxy upper with the cyan smart-link bar on top and a straight magazine.
  smart(r, magOut, ext) {
    r(-11, -5, 5, 1, lo); r(-11, -5, 1, 4, lo); r(-11, -2, 5, 1, lo); // skeletal stock
    r(-6, -6, 18, 4, m); r(-6, -6, 18, 1, hi); r(-6, -3, 18, 1, lo); r(4, -5, 3, 1, ink);
    r(0, -8, 7, 2, lo); r(1, -8, 5, 1, K.cyan); r(6, -7, 1, 1, K.white); // smart-link sensor
    r(12, -5, 5, 2, lo); r(13, -5, 1, 1, ink); r(15, -5, 1, 1, ink);
    guard(r, 0, -2); grip(r, -3, -2, 5, lo, m);
    if (!magOut) { r(4, -2, 3, ext ? 8 : 6, lo); r(4, -2, 1, ext ? 8 : 6, hi); }
    return { top: [-4, -6], muzzle: [17, -4] };
  },
  // K-5 Kaze: slim tube receiver, fat vented handguard, hooded front sight, forward-curved mag, two-rail stock.
  k5(r, magOut, ext) {
    r(-12, -6, 1, 4, m); r(-11, -6, 6, 1, lo); r(-11, -3, 6, 1, lo); // retractable stock
    r(-5, -6, 13, 3, m); r(-5, -6, 13, 1, hi); r(-5, -4, 13, 1, lo); r(2, -5, 3, 1, ink);
    r(-4, -8, 2, 2, lo); r(-4, -8, 1, 1, hi); // drum rear sight
    r(8, -6, 6, 3, poly); r(8, -6, 6, 1, polyHi); r(9, -5, 1, 1, ink); r(11, -5, 1, 1, ink);
    r(8, -7, 8, 1, lo); r(14, -8, 1, 1, m); r(14, -6, 3, 2, lo); r(14, -6, 3, 1, m);
    guard(r, -1, -3); grip(r, -3, -3, 6, poly, polyHi);
    if (!magOut) { r(2, -3, 3, 2, lo); r(3, -1, 3, 2, lo); r(4, 1, 3, 2, lo); if (ext) r(5, 3, 3, 2, lo); r(2, -3, 1, 2, hi); r(3, -1, 1, 2, hi); r(4, 1, 1, 2, hi); }
    return { top: [-2, -6], muzzle: [17, -5] };
  },
  // S-10 Alley Cat: a tall square box, magazine through the grip, a stub barrel and a folded wire stock.
  s10(r, magOut, ext) {
    r(-8, -7, 4, 1, hi); r(-8, -7, 1, 3, hi); // folded wire stock
    r(-4, -7, 11, 5, m); r(-4, -7, 11, 1, hi); r(-4, -3, 11, 1, lo); r(-4, -7, 1, 5, lo);
    r(1, -5, 3, 1, ink); r(3, -8, 1, 1, hi); r(-3, -6, 2, 1, K.pink);
    r(7, -5, 3, 2, lo); r(7, -5, 3, 1, m); r(8, -4, 1, 1, ink); // threaded stub
    r(5, -2, 1, 3, lo); // strap loop
    guard(r, 1, -2); grip(r, -2, -2, 4, lo, m, 0);
    if (!magOut) { r(-2, 2, 3, ext ? 6 : 4, lo); r(-2, 2, 1, ext ? 6 : 4, hi); }
    return { top: [0, -7], muzzle: [10, -4] };
  },
  // V-47 Medved: wood stock, handguard and grip, gas tube, post front sight, slant brake and a banana mag.
  v47(r, magOut, ext) {
    for (let i = 0; i < 4; i++) r(-14, -5 + i, 10, 1, i === 0 ? woodHi : i === 3 ? woodLo : wood); r(-14, -5, 1, 5, woodLo); r(-14, -1, 3, 1, wood);
    r(-4, -6, 13, 3, m); r(-4, -6, 13, 1, hi); r(-4, -4, 13, 1, lo); r(2, -5, 4, 1, ink); r(3, -5, 1, 1, K.amber);
    r(9, -7, 8, 1, lo); r(9, -6, 7, 3, wood); r(9, -6, 7, 1, woodHi); r(9, -4, 7, 1, woodLo);
    r(16, -5, 6, 1, m); r(20, -7, 1, 2, m); r(22, -6, 2, 2, lo); r(22, -6, 1, 1, hi);
    guard(r, -1, -3); grip(r, -3, -3, 5, wood, woodHi, 2);
    if (!magOut) { const n = ext ? 5 : 4; for (let i = 0; i < n; i++) { r(2 + i, -3 + i * 2, 3, 2, lo); r(2 + i, -3 + i * 2, 1, 2, hi); } }
    return { top: [-1, -6], muzzle: [24, -5] };
  },
  // VS-12 Kaban: the same Severka body in black polymer, a thick round barrel, a big straight box mag, pink top strip.
  vs12(r, magOut, ext) {
    r(-13, -5, 9, 2, poly); r(-13, -5, 9, 1, polyHi); r(-13, -5, 2, 5, poly); r(-12, -3, 1, 2, polyLo); // folding stock
    r(-4, -6, 13, 3, m); r(-4, -6, 13, 1, hi); r(-4, -4, 13, 1, lo); r(2, -5, 4, 1, ink); r(3, -5, 2, 1, K.amber);
    r(-2, -7, 4, 1, K.red);
    r(9, -6, 6, 3, poly); r(9, -6, 6, 1, polyHi); r(10, -4, 4, 1, polyLo);
    r(15, -6, 7, 3, m); r(15, -6, 7, 1, hi); r(15, -4, 7, 1, lo); r(21, -7, 1, 1, m);
    guard(r, -1, -3); grip(r, -3, -3, 5, poly, polyHi, 2);
    if (!magOut) { r(2, -3, 5, ext ? 7 : 5, poly); r(2, -3, 1, ext ? 7 : 5, polyHi); r(3, -1, 4, 1, polyLo); r(3, 1, 4, 1, polyLo); }
    return { top: [-1, -6], muzzle: [22, -5] };
  },
  // Tsurugi Coilgun (original): an angled stock, a cyan coil sleeve with lit rings and a battery pack, no magazine.
  charge(r, magOut) {
    r(-12, -6, 7, 2, lo); r(-12, -4, 2, 3, lo); r(-12, -6, 7, 1, m);
    r(-5, -6, 10, 4, m); r(-5, -6, 10, 1, hi); r(-5, -3, 10, 1, lo); r(-3, -5, 2, 1, K.cyan);
    r(5, -7, 15, 5, ink); r(6, -6, 13, 3, lo);
    for (let x = 6; x < 19; x += 3) { r(x, -7, 1, 5, dk(K.cyan, 0.3)); r(x, -6, 1, 3, K.cyan); r(x, -6, 1, 1, K.white); }
    r(20, -6, 2, 3, lo); r(20, -6, 2, 1, m);
    guard(r, -1, -2); grip(r, -3, -2, 5, lo, m);
    if (!magOut) { r(1, -2, 4, 3, lo); r(2, -1, 1, 1, K.cyan); }
    return { top: [-3, -6], muzzle: [22, -5] };
  },
};

function outline(src: HTMLCanvasElement): HTMLCanvasElement {
  const [c, g] = canvas(src.width, src.height), [mk, mg] = canvas(src.width, src.height);
  mg.drawImage(src, 0, 0); mg.globalCompositeOperation = 'source-in'; mg.fillStyle = K.ink; mg.fillRect(0, 0, src.width, src.height);
  for (const [x, y] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) g.drawImage(mk, x, y);
  g.drawImage(src, 0, 0); return c;
}

const cache = new Map<string, HeldGun>();
export function heldGun(family: HeldFamily, magOut = false, parts: HeldParts = {}): HeldGun {
  const key = `${family}|${+magOut}|${+!!parts.optic}|${+!!parts.suppressor}|${+!!parts.extMag}`, hit = cache.get(key); if (hit) return hit;
  const [c, g] = canvas(W, H);
  const r: R = (x, y, w, h, col) => px(g as G, OX + x, OY + y, w, h, col);
  const mounts = FAMILY[family](r, magOut, !!parts.extMag);
  let [mx, my] = mounts.muzzle;
  if (parts.suppressor) {
    const len = family === 'pistol' || family === 'p11' || family === 's10' ? 5 : 7;
    r(mx, my - 1, len, 3, lo); r(mx, my - 1, len, 1, hi); r(mx + 2, my, 1, 1, ink); r(mx + len - 1, my - 1, 1, 3, m);
    mx += len;
  }
  if (parts.optic) {
    const [ox, oy] = mounts.top, pistol = family === 'pistol' || family === 'p11';
    if (pistol) { r(ox, oy - 2, 3, 2, lo); r(ox + 1, oy - 2, 1, 1, K.cyan); }
    else { r(ox, oy - 1, 5, 1, ink); r(ox, oy - 3, 5, 2, lo); r(ox, oy - 3, 5, 1, m); r(ox + 4, oy - 3, 1, 2, K.cyan); r(ox, oy - 3, 1, 1, hi); }
  }
  const out: HeldGun = { canvas: outline(c), grip: { x: OX, y: OY }, muzzle: { x: OX + mx, y: OY + my } };
  cache.set(key, out); return out;
}
